// src/features/auth/SetPassword.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Button, 
  Alert,
  Spinner
} from 'react-bootstrap';
import { 
  Lock, 
  CheckCircle,
  XCircle,
  Shield,
  Sparkles,
  Eye,
  EyeOff,
  Key
} from 'lucide-react';
import { businessService } from '../../services';
import './SetPassword.css';

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [email, setEmail] = useState('');

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setError('No reset token found. Please check your link.');
      setVerifying(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      const result = await businessService.verifyResetToken(token);
      if (result.success) {
        setEmail(result.data.email);
        setIsValidToken(true);
        setError('');
      } else {
        setError(result.message || 'Invalid or expired reset link.');
        setIsValidToken(false);
      }
    } catch (err) {
      setError('Failed to verify reset link. Please try again.');
      setIsValidToken(false);
    }
    setVerifying(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await businessService.resetPassword(token, newPassword, confirmPassword);
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(result.message || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
    setLoading(false);
  };

  // Show loading state while verifying
  if (verifying) {
    return (
      <div className="set-password-page">
        <div className="set-password-container">
          <Card className="set-password-card">
            <Card.Body className="set-password-body" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <Spinner animation="border" style={{ color: '#0ea5e9', width: '48px', height: '48px' }} />
              <h4 style={{ marginTop: '20px', color: '#1a1e2b' }}>Verifying reset link...</h4>
              <p style={{ color: '#64748b', marginTop: '8px' }}>Please wait while we confirm your request.</p>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  // Show invalid token state
  if (!isValidToken) {
    return (
      <div className="set-password-page">
        <div className="set-password-container">
          <Card className="set-password-card">
            <Card.Body className="set-password-body" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <XCircle size={32} color="#ef4444" />
              </div>
              <h4 style={{ color: '#1a1e2b', marginBottom: '8px' }}>Invalid Reset Link</h4>
              <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto' }}>{error}</p>
              <Button 
                variant="primary" 
                style={{ marginTop: '20px' }}
                onClick={() => navigate('/forgot-password')}
              >
                Request New Link
              </Button>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="set-password-page">
      <div className="set-password-container">
        <Card className="set-password-card">
          <Card.Body className="set-password-body">
            {/* Header */}
            <div className="set-password-header">
              <div className="set-password-icon">
                <Key size={28} color="white" />
              </div>
              <div>
                <h1 className="set-password-title">Set New Password</h1>
                <p className="set-password-subtitle">
                  Create a new password for your account
                </p>
              </div>
            </div>

            {/* Email Info */}
            {email && (
              <div className="set-password-email-info">
                <span>Resetting password for:</span>
                <strong>{email}</strong>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <Alert className="set-password-success">
                <CheckCircle size={18} />
                Password reset successfully! Redirecting to login...
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert className="set-password-error">
                <XCircle size={18} />
                {error}
              </Alert>
            )}

            {/* Form */}
            <Form onSubmit={handleSubmit}>
              <div className="set-password-form-group">
                <Form.Label className="set-password-label">
                  New Password
                </Form.Label>
                <div className="set-password-password-wrapper">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create new password (min 8 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="set-password-input set-password-password-input"
                    required
                    disabled={loading || success}
                  />
                  <button
                    type="button"
                    className="set-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="set-password-hint">
                  Password must be at least 8 characters
                </div>
              </div>

              <div className="set-password-form-group">
                <Form.Label className="set-password-label">
                  Confirm Password
                </Form.Label>
                <div className="set-password-password-wrapper">
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="set-password-input set-password-password-input"
                    required
                    disabled={loading || success}
                  />
                  <button
                    type="button"
                    className="set-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || success}
                className="set-password-submit"
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Lock size={18} />
                    Reset Password
                  </>
                )}
              </Button>
            </Form>

            {/* Footer */}
            <div className="set-password-footer">
              <span>
                <Shield size={14} />
                Secure connection
              </span>
              <span>
                <Sparkles size={14} />
                Password recovery
              </span>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}